// Translation Provider Context
// Manages translation loading and display for frontend forms
import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {formSubmissionService} from "@services/form-submission.service.ts";

interface TranslationContextValue {
    currentLanguage: string;
    translations: Record<string, string>;
    isLoading: boolean;
    setLanguage: (languageCode: string) => void;
    translate: (entityType: string, entityId: number | string, fieldName: string, defaultValue: string) => string;
}

const TranslationContext = createContext<TranslationContextValue | undefined>(undefined);

interface TranslationProviderProps {
    children: ReactNode;
    formId: number;
    defaultLanguage?: string;
    detectionMethod?: 'browser' | 'user' | 'url' | 'manual' | 'joomla';
    enableLanguageSwitcher?: boolean;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
    children,
    formId,
    defaultLanguage = 'en-GB',
    detectionMethod = 'browser',
    enableLanguageSwitcher = true,
}) => {
    const [currentLanguage, setCurrentLanguage] = useState<string>(defaultLanguage);
    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    // Detect user's preferred language
    useEffect(() => {
        const detectLanguage = (): string => {
            switch (detectionMethod) {
                case 'joomla':
                    // Use language code from Joomla options (set by backend)
                    const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms') || {};
                    const joomlaLang = joomlaOptions.languageCode;
                    if (joomlaLang) return joomlaLang;
                    break;

                case 'browser':
                    // Use browser's language preference
                    const browserLang = navigator.language || navigator.languages?.[0];
                    return browserLang || defaultLanguage;

                case 'url':
                    // Check URL parameter (e.g., ?lang=fr-FR)
                    const urlParams = new URLSearchParams(window.location.search);
                    const urlLang = urlParams.get('lang');
                    if (urlLang) return urlLang;
                    break;

                case 'user':
                    // Get from user session/profile (would need backend integration)
                    // For now, fall back to browser
                    return navigator.language || defaultLanguage;

                case 'manual':
                    // Don't auto-detect, use default
                    return defaultLanguage;
            }

            return defaultLanguage;
        };

        const detectedLanguage = detectLanguage();

        // Only set if different from default and language switcher is enabled
        if (enableLanguageSwitcher && detectedLanguage !== defaultLanguage) {
            setCurrentLanguage(detectedLanguage);
        }
    }, [defaultLanguage, detectionMethod, enableLanguageSwitcher]);

    // Load translations when language changes
    useEffect(() => {
        const loadTranslations = async () => {
            // Don't load translations if we're on the default language
            if (currentLanguage === defaultLanguage) {
                setTranslations({});
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const formTranslations = await formSubmissionService.getFormTranslations(
                    formId,
                    currentLanguage
                );

                // Flatten all translations into a single map
                const translationMap: Record<string, string> = {};

                // Map form translations
                formTranslations.form.forEach((trans) => {
                    const key = `form_${trans.entity_id}_${trans.field_name}`;
                    translationMap[key] = trans.translation;
                });

                // Map section translations
                formTranslations.sections.forEach((trans) => {
                    const key = `section_${trans.entity_id}_${trans.field_name}`;
                    translationMap[key] = trans.translation;
                });

                // Map field translations
                formTranslations.fields.forEach((trans) => {
                    const key = `field_${trans.entity_id}_${trans.field_name}`;
                    translationMap[key] = trans.translation;
                });

                // Map option translations
                // Note: entity_id can be string (for options stored in JSON)
                if (formTranslations.options) {
                    formTranslations.options.forEach((trans) => {
                        // Convert entity_id to string to handle both numeric and string IDs
                        const entityId = String(trans.entity_id);
                        const key = `option_${entityId}_${trans.field_name}`;
                        translationMap[key] = trans.translation;
                    });
                }

                // Map row translations
                if (formTranslations.rows) {
                    formTranslations.rows.forEach((trans) => {
                        // Convert entity_id to string to handle both numeric and string IDs
                        const entityId = String(trans.entity_id);
                        const key = `row_${entityId}_${trans.field_name}`;
                        translationMap[key] = trans.translation;
                    });
                }

                // Map column translations
                if (formTranslations.columns) {
                    formTranslations.columns.forEach((trans) => {
                        // Convert entity_id to string to handle both numeric and string IDs
                        const entityId = String(trans.entity_id);
                        const key = `column_${entityId}_${trans.field_name}`;
                        translationMap[key] = trans.translation;
                    });
                }

                setTranslations(translationMap);
            } catch (error) {
                console.error('Failed to load translations:', error);
                setTranslations({});
            } finally {
                setIsLoading(false);
            }
        };

        loadTranslations();
    }, [formId, currentLanguage, defaultLanguage]);

    // Helper function to get translation
    const translate = (
        entityType: string,
        entityId: number | string,
        fieldName: string,
        defaultValue: string
    ): string => {
        // If on default language, return default value
        if (currentLanguage === defaultLanguage) {
            return defaultValue;
        }

        // Build translation key - convert entityId to string to handle both types
        const key = `${entityType}_${String(entityId)}_${fieldName}`;

        // Return translation or default value
        return translations[key] || defaultValue;
    };

    const setLanguage = (languageCode: string) => {
        setCurrentLanguage(languageCode);
    };

    return (
        <TranslationContext.Provider
            value={{
                currentLanguage,
                translations,
                isLoading,
                setLanguage,
                translate,
            }}
        >
            {children}
        </TranslationContext.Provider>
    );
};

// Hook to use translations
export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within TranslationProvider');
    }
    return context;
};

